import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { basisTerminationService } from "../services/basisTermination";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import LoadingButton from '@shared/components/LoadingButton';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

export default function BasisForTerminationForm({ onClose, onConfirm, editData, formProps }) {
    const { t } = useTranslation();
    const validationSchema = yup.object({
        reasonKt: yup.string().required(t('common.required')),
        description: yup.string().required(t('common.required')),
        code: yup.string().required(t('common.required')),
        hrNssiReasonsId: yup.string().required(t('common.required')),
    });
    const { modalRef } = useFormHook({}, onClose);
    const { register, handleSubmit, setValue, reset, formState: { errors } } =
        useForm({
            resolver: yupResolver(validationSchema),
            defaultValues: {
                active: true
            },
            ...formProps
        });
    const [hrNssiReasons, setHrNssiReasons] = useState([]);
    const [reasonLeaving, setReasonLeaving] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [nssiData, leavingData] = await Promise.all([
                    basisTerminationService.getHrNssiReasons(),
                    basisTerminationService.getReasonLeaving()
                ]);
                setHrNssiReasons(nssiData);
                setReasonLeaving(leavingData);
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
            }
        };
        fetchDropdownData();
    }, []);

    // Fetch data if editing
    useEffect(() => {
        const fetchBasisTerminationData = async () => {
            if (editData && editData.id) {
                try {
                    setFetchingData(true);
                    const data = await basisTerminationService.getBasisTerminationById(editData.id);
                    
                    setValue('reasonKt', data.reasonKt || '');
                    setValue('description', data.description || '');
                    setValue('code', data.code || '');
                    setValue('hrNssiReasonsId', data.hrNssiReasonsId || '');
                    setValue('sgebReasonLeavingCode', data.sgebReasonLeavingCode || '');
                    setValue('codeTerminationInsurance', data.codeTerminationInsurance !== undefined && data.codeTerminationInsurance !== null ? data.codeTerminationInsurance.toString() : '');
                    setValue('active', data.active !== undefined ? data.active : true);
                } catch (error) {
                    console.error('Error fetching basis termination data:', error);
                } finally {
                    setFetchingData(false);
                }
            } else {
                reset({
                    active: true,
                    codeTerminationInsurance: ''
                });
            }
        };

        fetchBasisTerminationData();
    }, [editData, setValue, reset]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const formData = {
                reasonKt: data.reasonKt,
                description: data.description,
                code: data.code,
                hrNssiReasonsId: parseInt(data.hrNssiReasonsId),
                sgebReasonLeavingCode: data.sgebReasonLeavingCode,
                codeTerminationInsurance: parseInt(data.codeTerminationInsurance),
                active: data.active
            };
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        } finally {
            setLoading(false);
        }
    }

    const codeTerminationInsuranceOptions = [
        { value: '0', label: '' },
        { value: '1', label: t('staff.basisForTermination.table.headers.codeTermination1')},
        { value: '2', label: t('staff.basisForTermination.table.headers.codeTermination2') }
    ];

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')}
                        </h4>
                    </div>
                    {fetchingData ? (
                        <div className="modal-body">
                            <p>{t('common.loading') || 'Loading...'}</p>
                        </div>
                    ) : (
                        <form id="basisTerminationForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span>{t('staff.basisForTermination.table.headers.reasonKt') || 'Reason under the Labour Code'}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={100}
                                            className={`form-control ${errors.reasonKt ? 'input-error' : ''}`}
                                            placeholder={errors.reasonKt ? errors.reasonKt.message : t('common.required')}
                                            {...register('reasonKt')} />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.basisForTermination.table.headers.description') || 'Description'}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={100}
                                            className={`form-control ${errors.description ? 'input-error' : ''}`}
                                            placeholder={errors.description ? errors.description.message : t('common.required')}
                                            {...register('description')} />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.basisForTermination.table.headers.code') || 'Code'}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={10}
                                            className={`form-control ${errors.code ? 'input-error' : ''}`}
                                            placeholder={errors.code ? errors.code.message : t('common.required')}
                                            {...register('code')} />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.basisForTermination.table.headers.hrNssiReasonsId') || 'Reason (NRA)'}<span className="required">*</span></span>
                                        <select className={`form-control ${errors.hrNssiReasonsId ? 'input-error' : ''}`}
                                            placeholder={errors.hrNssiReasonsId ? errors.hrNssiReasonsId.message : t('common.required')}
                                            {...register('hrNssiReasonsId')}>
                                            <option value="">{t('common.select')}</option>
                                            {hrNssiReasons.map((reason) => (
                                                <option key={reason.id} value={reason.id}>{reason.reasonId} - {reason.description}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span>{t('staff.basisForTermination.table.headers.sgebReasonLeavingCode') || 'Reason for leaving'}</span>
                                        <select className={`form-control ${errors.sgebReasonLeavingCode ? 'input-error' : ''}`}
                                            placeholder={errors.sgebReasonLeavingCode}
                                            {...register('sgebReasonLeavingCode')}>
                                            <option value="">{t('common.select')}</option>
                                            {reasonLeaving.map((reason) => (
                                                <option key={reason.code || reason.value} value={reason.code || reason.value}>
                                                    {reason.code || reason.value} - {reason.longDescription || reason.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.basisForTermination.table.headers.codeTerminationInsurance') || 'Code termination of social security'}</span>
                                        <select className={`form-control ${errors.codeTerminationInsurance ? 'input-error' : ''}`}
                                            placeholder={errors.codeTerminationInsurance}
                                            {...register('codeTerminationInsurance')}>
                                            <option value="">{t('common.select')}</option>
                                            {codeTerminationInsuranceOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                {...register('active')}
                                            />
                                            <label htmlFor="active">{t('staff.basisForTermination.table.headers.active') || 'Active'}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="basisTerminationForm" className="btn btn-primary" loading={loading} disabled={fetchingData}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

